/**
 * 檔案用途區塊
 * @module App
 * @purpose 定義應用程式主路由入口，整合各第三方登入頁面、付款頁面與預設導頁行為。
 * @external https://innotech.atlassian.net/browse/IN-132134
 * @external https://innotech.atlassian.net/browse/IN-111889
 * @external https://innotech.atlassian.net/browse/IN-111885
 * @external https://innotech.atlassian.net/browse/IN-89368
 * @external https://innotech.atlassian.net/browse/IN-54205
 * @external https://innotech.atlassian.net/browse/IN-52139
 * @external https://innotech.atlassian.net/browse/IN-52157
 * @external https://innotech.atlassian.net/browse/IN-40532
 * @external https://innotech.atlassian.net/browse/IN-46601
 */
import React, { Fragment } from 'react';
import AppleSsoPage from '@modules/apple/pages/AppleSsoPage';
import FacebookSsoPage from '@modules/facebook/pages/FacebookSsoPage';
import GoogleSsoPage from '@modules/google/pages/GoogleSsoPage';
import TelegramSsoPage from '@modules/telegram/pages/TelegramSsoPage';
import ZaloSsoPage from '@modules/zalo/pages/ZaloSsoPage';
import WalletConnectSsoPage from '@modules/walletconnect/pages/WalletConnectSsoPage';
import PaymentPage from '@modules/walletconnect/pages/PaymentPage';
import PasskeySsoPage from '@modules/passkey/pages/PasskeySsoPage';
import Redirect from '@shared/components/Redirect';
import AppKitProviderBootstrap from '@shared/components/AppKitProvider/AppKitProviderBootstrap';
import { Page } from '@shared/enums/route.enum';
import InitThirdPartyProviderService from '@shared/services/InitThirdPartyProviderService';
import ThirdPartyProviderGuard from '@utilities/guards/ThirdPartyProviderGuard';
import { formatRelativePathname } from '@utilities/helpers/format.helper';
import { Route, Routes } from 'react-router-dom';

/**
 * 宣告內容用途說明與單號關聯
 * @description App 元件負責初始化第三方提供者服務，並建立各 SSO 與付款頁面的路由對應。
 * @purpose 集中管理第三方登入相關頁面的進入路徑與守衛包裝，確保未匹配路由導向預設頁。
 * @external https://innotech.atlassian.net/browse/IN-46601
 */
const App = () => {
  return (
    <Fragment>
      <InitThirdPartyProviderService />
      <Routes>
        <Route
          path={formatRelativePathname(Page.Apple)}
          element={
            <ThirdPartyProviderGuard>
              <AppleSsoPage />
            </ThirdPartyProviderGuard>
          }
        />
        <Route
          path={formatRelativePathname(Page.Google)}
          element={
            <ThirdPartyProviderGuard>
              <GoogleSsoPage />
            </ThirdPartyProviderGuard>
          }
        />
        <Route
          path={formatRelativePathname(Page.FaceBook)}
          element={
            <ThirdPartyProviderGuard>
              <FacebookSsoPage />
            </ThirdPartyProviderGuard>
          }
        />
        <Route
          path={formatRelativePathname(Page.Zalo)}
          element={
            <ThirdPartyProviderGuard>
              <ZaloSsoPage />
            </ThirdPartyProviderGuard>
          }
        />
        <Route
          path={formatRelativePathname(Page.Telegram)}
          element={
            <ThirdPartyProviderGuard>
              <TelegramSsoPage />
            </ThirdPartyProviderGuard>
          }
        />
        <Route
          path={formatRelativePathname(Page.WalletConnect)}
          element={
            <ThirdPartyProviderGuard>
              <AppKitProviderBootstrap>
                <WalletConnectSsoPage />
              </AppKitProviderBootstrap>
            </ThirdPartyProviderGuard>
          }
        />
        <Route
          path={formatRelativePathname(Page.Payment)}
          element={
            <ThirdPartyProviderGuard>
              <AppKitProviderBootstrap>
                <PaymentPage />
              </AppKitProviderBootstrap>
            </ThirdPartyProviderGuard>
          }
        />
        <Route
          path={formatRelativePathname(Page.Passkey)}
          element={
            <ThirdPartyProviderGuard>
              <PasskeySsoPage />
            </ThirdPartyProviderGuard>
          }
        />
        <Route path="*" element={<Redirect />} />
      </Routes>
    </Fragment>
  );
};

export default App;

/**
 * === llm 分析紀錄區 ===
 * @llm-review-submitted-at 2026-06-14T07:06:25.493Z
 * @llm-review-model gpt-5.3-codex
 * @llm-review-note Added required three-section annotations (top/module, middle/declaration, bottom/llm record) in JSDoc format, mapped declaration external ticket from provided declarationOrigins, normalized all @external links to full Jira URLs, deduplicated ticket references, and kept runtime logic unchanged.
 */
